import { computed, inject } from '@angular/core'

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'

import { Subscription } from 'rxjs'

import { ComposerFileUploaderService } from '@flowx/designer/modules/ai-builder/services/composer-file-uploader.service'
import {
  ComposerFile,
  FileRefusal,
  FilesInConversation,
} from '@flowx/designer/modules/ai-builder/types/composer-file.types'
import {
  HarnessAttachmentsPolicy,
  HarnessSentFile,
} from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import { allFilesReady } from '@flowx/designer/modules/ai-builder/utils/file-chip-view.util'
import {
  alreadyInConversation,
  applyUploadEvent,
  pickFiles,
  pickedHash,
  resentRepeats,
  sentFile,
} from '@flowx/designer/modules/ai-builder/utils/file-pick.util'

type HarnessComposerFilesState = {
  /** What files a message may carry. Deployment-wide, so it outlives every conversation: written
   * by any session read or conversation-list page that carries it, and null until one has — the
   * composer offers no attach till then. */
  attachmentsPolicy: HarnessAttachmentsPolicy | null
  /** The files on the composer, each uploaded as a temp file. A send takes them with the message;
   * a refused one keeps them on its not-run message, not here. */
  composerFiles: readonly ComposerFile[]
  /** The workspace the composer's files went up in; undefined while there are none. */
  workspaceId: string | undefined
  /** What the last pick turned away, one line a file. */
  fileRefusals: readonly FileRefusal[]
  /** The composer chip or file panel row a notice's Show points at, while it is highlighted. */
  highlightedFileId: string | null
  /** Picks still being read before they become chips: Send waits for them too. */
  pendingPickCount: number
}

const initialComposerFilesState: HarnessComposerFilesState = {
  attachmentsPolicy: null,
  composerFiles: [],
  workspaceId: undefined,
  fileRefusals: [],
  highlightedFileId: null,
  pendingPickCount: 0,
}

/** The files drafted on the composer, from the pick to the send. What the conversation already
 * holds and the workspace a turn acts in are handed in by the session; this leaf owns the chips,
 * their uploads and the notice. */
export const HarnessComposerFilesStore = signalStore(
  { providedIn: 'root' },
  withState(initialComposerFilesState),
  withComputed(({ composerFiles, pendingPickCount }) => ({
    /** Every file is attached and no pick is still being read: the only state a message goes out
     * in. */
    readyToSend: computed(() => allFilesReady(composerFiles()) && !pendingPickCount()),
  })),
  withMethods((store, uploader = inject(ComposerFileUploaderService)) => {
    // One upload per composer file, by its local id, so taking the chip off cancels it.
    const uploads = new Map<string, Subscription>()

    // Bumped whenever the draft is emptied, so a pick still being read when it was lands nowhere.
    let draftGeneration = 0

    let highlightTimer: ReturnType<typeof setTimeout> | undefined

    /** Hand one file to the uploader, in the workspace the composer's files go up in, and fold
     * what it reports into its chip. */
    const startUpload = (file: ComposerFile): void => {
      uploads.set(
        file.id,
        uploader.upload(file.file, store.workspaceId()).subscribe((event) =>
          patchState(store, (state) => ({
            composerFiles: state.composerFiles.map((draft) =>
              draft.id === file.id ? applyUploadEvent(draft, event) : draft
            ),
          }))
        )
      )
    }

    /** Take one file off the composer, cancelling its upload. */
    const remove = (id: string): void => {
      uploads.get(id)?.unsubscribe()
      uploads.delete(id)
      patchState(store, (state) => ({
        composerFiles: state.composerFiles.filter((file) => file.id !== id),
      }))
    }

    /** Point at a chip or a panel row for a moment, from the latest Show. */
    const highlight = (id: string): void => {
      clearTimeout(highlightTimer)
      patchState(store, { highlightedFileId: id })
      highlightTimer = setTimeout(
        () => patchState(store, { highlightedFileId: null }),
        SHOWN_FILE_HIGHLIGHT_MS
      )
    }

    /** An empty composer and a closed notice. */
    const emptyDraft = (): void => {
      draftGeneration += 1
      clearTimeout(highlightTimer)
      patchState(store, {
        composerFiles: [],
        workspaceId: undefined,
        fileRefusals: [],
        highlightedFileId: null,
      })
    }

    /** Take every file off the composer, cancelling their uploads, and close the notice. A
     * pick still being read is left counting: it is dropped when it lands. */
    const clear = (): void => {
      uploads.forEach((upload) => upload.unsubscribe())
      uploads.clear()
      emptyDraft()
    }

    return {
      /** The policy a session read or a conversation-list page carried, when it carried one. One
       * that turns attachments off takes the files off the composer, which no longer shows them. */
      adoptAttachmentsPolicy: (policy: HarnessAttachmentsPolicy | null | undefined): void => {
        if (policy) {
          patchState(store, { attachmentsPolicy: policy })
        }

        if (policy && !policy.enabled) {
          clear()
        }
      },

      /** Put files on the composer: the ones the policy admits start uploading at once, in the
       * workspace the turn acts in once their bytes are read; the rest are the notice's, one
       * line a file. */
      attach: async (
        incoming: readonly File[],
        inConversation: () => FilesInConversation,
        workspaceId: () => string | undefined
      ): Promise<void> => {
        const policy = store.attachmentsPolicy()

        if (!policy?.enabled) {
          return
        }

        const generation = draftGeneration

        patchState(store, (state) => ({ pendingPickCount: state.pendingPickCount + 1 }))
        const hashed = await Promise.all(
          incoming.map(async (file) => ({ file, hash: await pickedHash(file, policy) }))
        ).finally(() =>
          patchState(store, (state) => ({ pendingPickCount: state.pendingPickCount - 1 }))
        )

        // Reading the bytes takes a moment; a pick whose draft was emptied meanwhile — cleared,
        // sent, or left with its conversation — is dropped.
        if (generation !== draftGeneration) {
          return
        }

        const { admitted, refusals } = pickFiles(
          policy,
          store.composerFiles(),
          hashed,
          inConversation()
        )

        patchState(store, (state) => ({
          composerFiles: [...state.composerFiles, ...admitted],
          workspaceId: state.workspaceId ?? workspaceId(),
          fileRefusals: refusals,
        }))
        admitted.forEach(startUpload)
      },

      remove,

      /** Send a chip whose upload failed recoverably up again, from the start. */
      retry: (id: string): void => {
        const file = store.composerFiles().find((draft) => draft.id === id)

        if (!file) {
          return
        }

        const restarted = { ...file, progress: 0, error: null, recoverable: false }

        patchState(store, (state) => ({
          composerFiles: state.composerFiles.map((draft) => (draft.id === id ? restarted : draft)),
        }))
        startUpload(restarted)
      },

      /** The composer's files, as the message that goes now carries them. Their uploads are
       * over, so nothing is cancelled; the composer is left empty. */
      take: (): readonly HarnessSentFile[] => {
        const workspaceId = store.workspaceId()
        const sent = store.composerFiles().map((file) => ({ ...sentFile(file), workspaceId }))

        uploads.clear()
        emptyDraft()

        return sent
      },

      /** A message sent again carries its own files: the composer's copies of them come off,
       * each a line in the notice. */
      refuseRepeats: (resent: readonly HarnessSentFile[]): void => {
        const repeats = resentRepeats(store.composerFiles(), resent)

        repeats.forEach((file) => remove(file.id))

        if (repeats.length) {
          patchState(store, (state) => ({
            fileRefusals: [...state.fileRefusals, ...repeats.map(alreadyInConversation)],
          }))
        }
      },

      clear,

      dismissRefusals: (): void => patchState(store, { fileRefusals: [] }),

      highlight,
    }
  })
)

/** How long a notice's Show keeps the chip it points at highlighted. */
export const SHOWN_FILE_HIGHLIGHT_MS = 2400
